// PDB解析工具函数
export interface Residue {
  id: number;
  name: string;
  code: string;
  chainId: string;
  position: number;
  secondaryStructure?: 'helix' | 'sheet' | 'loop';
  selected?: boolean;
  highlighted?: boolean;
}

export interface Chain {
  id: string;
  name: string;
  sequence: Residue[];
  color: string;
}

// 获取氨基酸单字母代码
export const getAminoAcidCode = (threeLetter: string): string => {
  const codes: { [key: string]: string } = {
    'ALA': 'A', 'ARG': 'R', 'ASN': 'N', 'ASP': 'D', 'CYS': 'C',
    'GLU': 'E', 'GLN': 'Q', 'GLY': 'G', 'HIS': 'H', 'ILE': 'I',
    'LEU': 'L', 'LYS': 'K', 'MET': 'M', 'PHE': 'F', 'PRO': 'P',
    'SER': 'S', 'THR': 'T', 'TRP': 'W', 'TYR': 'Y', 'VAL': 'V'
  };
  return codes[threeLetter] || 'X';
};

// 从mmCIF数据提取序列信息（简易解析）
export const extractSequenceFromCIF = (cifData: string): Chain[] => {
  const lines = cifData.split(/\r?\n/);
  const chains: { [key: string]: Chain } = {};

  // 简单的 token 解析，支持引号包裹
  const tokenize = (line: string): string[] => {
    const tokens = line.match(/'(?:[^']|'')*'|"(?:[^"]|"")*"|\S+/g) || [];
    return tokens.map(t => {
      const s = t.trim();
      if ((s.startsWith("'") && s.endsWith("'")) || (s.startsWith('"') && s.endsWith('"'))) {
        return s.slice(1, -1);
      }
      return s;
    });
  };

  let inAtomLoop = false;
  let headers: string[] = [];
  // 分别记录 label 与 auth 的列索引，优先使用 label
  let chainIdxLabel = -1, chainIdxAuth = -1;
  let resSeqIdxLabel = -1, resSeqIdxAuth = -1;
  let resNameIdxLabel = -1, resNameIdxAuth = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    if (line === 'loop_') {
      // 可能进入新loop
      inAtomLoop = false;
      headers = [];
      chainIdxLabel = chainIdxAuth = -1;
      resSeqIdxLabel = resSeqIdxAuth = -1;
      resNameIdxLabel = resNameIdxAuth = -1;
      // 继续读取 header 行
      let j = i + 1;
      while (j < lines.length) {
        const l = lines[j].trim();
        if (l.startsWith('_')) {
          headers.push(l);
          j++;
          continue;
        }
        break;
      }
      // 判断是否是 _atom_site loop
      if (headers.some(h => h.startsWith('_atom_site.'))) {
        inAtomLoop = true;
        // 记录所需列索引
        headers.forEach((h, idx) => {
          if (h.endsWith('label_asym_id')) chainIdxLabel = idx;
          if (h.endsWith('auth_asym_id')) chainIdxAuth = idx;
          if (h.endsWith('label_seq_id')) resSeqIdxLabel = idx;
          if (h.endsWith('auth_seq_id')) resSeqIdxAuth = idx;
          if (h.endsWith('label_comp_id')) resNameIdxLabel = idx;
          if (h.endsWith('auth_comp_id')) resNameIdxAuth = idx;
        });
        // 从 j 开始读取数据行
        i = j - 1;
        continue;
      }
    }

    if (inAtomLoop) {
      if (line.startsWith('_') || line === 'loop_') {
        // 新的loop开始，结束解析
        inAtomLoop = false;
        continue;
      }
      const cols = tokenize(line);
      const ci = chainIdxLabel >= 0 ? chainIdxLabel : chainIdxAuth;
      const ri = resSeqIdxLabel >= 0 ? resSeqIdxLabel : resSeqIdxAuth;
      const ni = resNameIdxLabel >= 0 ? resNameIdxLabel : resNameIdxAuth;
      if (ci < 0 || ri < 0 || ni < 0) continue;
      if (cols.length < headers.length) continue;
      const chainId = (cols[ci] || 'A').toString();
      const resSeqRaw = cols[ri];
      const resName = (cols[ni] || 'UNK').toString();
      if (!chainId || !resSeqRaw || resSeqRaw === '?' || resSeqRaw === '.') continue;
      const resSeq = parseInt(resSeqRaw as string, 10);
      if (isNaN(resSeq)) continue;

      if (!chains[chainId]) {
        chains[chainId] = {
          id: chainId,
          name: `Chain ${chainId}`,
          sequence: [],
          color: getChainColor(chainId)
        };
      }

      if (!chains[chainId].sequence.find(r => r.position === resSeq)) {
        chains[chainId].sequence.push({
          id: resSeq,
          name: resName,
          code: getAminoAcidCode(resName),
          chainId,
          position: resSeq,
        });
      }
    }
  }

  Object.values(chains).forEach(chain => {
    chain.sequence.sort((a, b) => a.position - b.position);
  });
  return Object.values(chains);
};

// 获取链颜色
export const getChainColor = (chainId: string): string => {
  const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'];
  return colors[chainId.charCodeAt(0) % colors.length];
};

// 从PDB数据提取序列信息
export const extractSequenceFromPDB = (pdbData: string): Chain[] => {
  const lines = pdbData.split('\n');
  const chains: { [key: string]: Chain } = {};
  
  lines.forEach(line => {
    if (line.startsWith('ATOM') || line.startsWith('HETATM')) {
      const chainId = line.substring(21, 22).trim() || 'A';
      const resName = line.substring(17, 20).trim();
      const resSeq = parseInt(line.substring(22, 26).trim());
      
      if (!chains[chainId]) {
        chains[chainId] = {
          id: chainId,
          name: `Chain ${chainId}`,
          sequence: [],
          color: getChainColor(chainId)
        };
      }
      
      // 避免重复添加相同位置的残基
      if (!chains[chainId].sequence.find(r => r.position === resSeq)) {
        chains[chainId].sequence.push({
          id: resSeq,
          name: resName,
          code: getAminoAcidCode(resName),
          chainId: chainId,
          position: resSeq
        });
      }
    }
  });
  
  // 按位置排序
  Object.values(chains).forEach(chain => {
    chain.sequence.sort((a, b) => a.position - b.position);
  });
  
  return Object.values(chains);
};
